import { EntityType } from "@prisma/client";
import { createContext, PropsWithChildren, useContext, useMemo, useState } from "react";

export enum ActionCenterTab {
  TASKS = "tasks",
  ALERTS = "alerts",
}

type ActionCenterState = {
  activeTab: ActionCenterTab;
  isOpen: boolean;
  currentEntityId: string | null;
  currentEntityType: EntityType | null;
};

type ActionCenterContextType = {
  state: ActionCenterState;
  setActiveTab: (tab: ActionCenterTab) => void;
  setCurrentEntityId: (entityId: string | null) => void;
  setCurrentEntityType: (payload: EntityType | null) => void;
  setIsOpen: (isOpen: boolean) => void;
};

const defaultState: ActionCenterState = {
  activeTab: ActionCenterTab.TASKS,
  isOpen: false,
  currentEntityId: null,
  currentEntityType: null,
};
const defaultVoidMethod = () => {
  return;
};

const ActionCenterContext = createContext<ActionCenterContextType>({
  state: { ...defaultState },
  setActiveTab: defaultVoidMethod,
  setIsOpen: defaultVoidMethod,
  setCurrentEntityId: defaultVoidMethod,
  setCurrentEntityType: defaultVoidMethod,
});

export const ActionCenterProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const [activeTab, setActiveTab] = useState<ActionCenterTab>(ActionCenterTab.TASKS);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentEntityId, setCurrentEntityId] = useState<string | null>(null);
  const [currentEntityType, setCurrentEntityType] = useState<EntityType | null>(null);

  const value = useMemo<ActionCenterContextType>(
    () => ({
      state: {
        activeTab,
        isOpen,
        currentEntityId,
        currentEntityType,
      },
      setActiveTab,
      setIsOpen,
      setCurrentEntityId,
      setCurrentEntityType,
    }),
    [activeTab, isOpen, currentEntityId, currentEntityType],
  );

  return <ActionCenterContext.Provider value={value}>{children}</ActionCenterContext.Provider>;
};

export const useActionCenter = () => {
  return useContext(ActionCenterContext);
};
